import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { TITLE_MAX, itemLabel, type AdminItem, type ItemCondition, type ItemStatus } from '@sellit/shared';
import { api, ApiError } from '../lib/api';
import { setAppTitle, STATUS_LABEL } from '../lib/format';
import { ConditionSelect } from './ConditionSelect';
import { PriceEntry, priceValueFrom, readPrice } from './PriceEntry';
import { PhotoPicker, storedPhoto, usePhotoUploads } from './usePhotoUploads';

const SELLING_STATUSES = ['AVAILABLE', 'PENDING', 'SOLD'] as const satisfies readonly ItemStatus[];

export function EditItemPage() {
  const { saleId = '', itemId = '' } = useParams();
  const [item, setItem] = useState<AdminItem | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    api.item(itemId).then(
      (i) => {
        setItem(i);
        setAppTitle(`Edit #${i.number}`);
      },
      (err: ApiError) => setLoadError(err.status === 404 ? 'This item was deleted.' : err.message),
    );
  }, [itemId]);

  if (loadError) return <main className="page narrow"><p className="error">{loadError}</p><Link to={`/admin/sales/${saleId}`}>Back to sale</Link></main>;
  if (!item) return <main className="page center muted">Loading…</main>;
  return <EditForm saleId={saleId} item={item} />;
}

function EditForm({ saleId, item }: { saleId: string; item: AdminItem }) {
  const navigate = useNavigate();
  const uploads = usePhotoUploads(saleId, item.images.map((img) => storedPhoto(img.id, img.thumbUrl)));
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const [condition, setCondition] = useState<ItemCondition | null>(item.condition);
  const [price, setPrice] = useState(() => priceValueFrom(item.pricingMode, item.priceCents));
  const [status, setStatus] = useState<ItemStatus>(item.status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const back = `/admin/sales/${saleId}`;
  const { pricing } = readPrice(price);
  const ready = uploads.photos.length > 0 && pricing !== null && !uploads.failed && !uploads.uploading;

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      navigate(back);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save. Try again.');
      if (err instanceof ApiError && err.fields) setFieldErrors(err.fields);
      setBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!ready || !pricing || busy) return;
    void run(() =>
      api.updateItem(item.id, {
        title,
        description,
        condition,
        ...pricing,
        status,
        imageIds: uploads.imageIds(),
      }),
    );
  }

  function remove() {
    if (window.confirm(`Delete ${itemLabel(item)} for good? Its photos and inquiries are deleted too.`)) {
      void run(() => api.deleteItem(item.id));
    }
  }

  const archived = item.status === 'ARCHIVED';

  return (
    <main className="page narrow">
      <header className="admin-header">
        <h1>Edit #{item.number}</h1>
        <Link className="link small" to={back}>Cancel</Link>
      </header>

      <form onSubmit={onSubmit} className="stack">
        {!archived && (
          <fieldset className="segmented" aria-label="Status">
            {SELLING_STATUSES.map((s) => (
              <label key={s} className={status === s ? 'on' : ''}>
                <input type="radio" name="status" value={s} checked={status === s} onChange={() => setStatus(s)} />
                {STATUS_LABEL[s]}
              </label>
            ))}
          </fieldset>
        )}
        {archived && <p className="notice small">This item is archived: buyers can't see it.</p>}

        <PhotoPicker uploads={uploads} error={fieldErrors.imageIds} />

        <PriceEntry value={price} onChange={setPrice} error={fieldErrors.priceCents} />

        <label className="field">
          <span>Item name (optional)</span>
          <input value={title} maxLength={TITLE_MAX} autoCapitalize="sentences" placeholder={`Item #${item.number}`} onChange={(e) => setTitle(e.target.value)} />
          {fieldErrors.title && <small className="error">{fieldErrors.title}</small>}
        </label>

        <label className="field">
          <span>Description (optional)</span>
          <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <ConditionSelect value={condition} onChange={setCondition} />

        {error && <p className="error" role="alert">{error}</p>}

        <button className="btn primary big" disabled={!ready || busy}>
          {uploads.uploading ? 'Uploading photos…' : busy ? 'Saving…' : 'Save changes'}
        </button>
      </form>

      <div className="stack danger-zone">
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => run(() => api.updateItem(item.id, { status: archived ? 'AVAILABLE' : 'ARCHIVED' }))}
        >
          {archived ? 'Unarchive (show to buyers again)' : 'Archive (hide from buyers)'}
        </button>
        <button type="button" className="btn danger" disabled={busy} onClick={remove}>
          Delete permanently
        </button>
      </div>
    </main>
  );
}
